// src/services/srsService.js
// Reusable Anki-style Spaced Repetition System Service
// Can be used for Vocabulary, Grammar, Redemittel, etc.

/**
 * Card States in Anki-style SRS
 */
export const CardState = {
  NEW: 'new',
  LEARNING: 'learning',
  REVIEW: 'review',
  RELEARNING: 'relearning'
};

/**
 * Review Ratings (Anki-style)
 */
export const Rating = {
  AGAIN: 1,    // Failed, repeat soon
  HARD: 2,     // Difficult but passed
  GOOD: 3,     // Normal difficulty
  EASY: 4      // Too easy
};

/**
 * Default SRS Configuration (can be customized per component)
 */
export const defaultConfig = {
  // New cards
  newSteps: [1, 10],                    // Learning steps in minutes
  graduatingInterval: 1,                // Days after graduating from learning
  easyInterval: 4,                      // Days for "Easy" on new cards

  // Reviews
  easyBonus: 1.3,                       // Multiplier for easy review
  intervalModifier: 1.0,                // Global interval modifier
  maximumInterval: 365,                 // Maximum days between reviews
  hardInterval: 1.2,                    // Multiplier for hard interval

  // Lapses (failed reviews)
  lapseSteps: [10],                     // Relearning steps in minutes
  lapseNewInterval: 0.7,                // Multiplier for new interval after lapse
  minimumInterval: 1,                   // Minimum interval in days

  // Ease factors
  startingEase: 250,                    // Starting ease (250 = 2.5x multiplier)
  minimumEase: 130,                     // Minimum ease factor
  easyPenalty: 15,                      // Ease penalty for "Hard" rating
  easyReward: 15,                       // Ease bonus for "Easy" rating
};

/**
 * Main Anki-style SRS Class
 */
export class AnkiSRS {
  constructor(config = {}) {
    this.config = { ...defaultConfig, ...config };
  }

  /**
   * Initialize a new card
   */
  initializeCard(cardId) {
    return {
      id: cardId,
      state: CardState.NEW,
      ease: this.config.startingEase,
      interval: 0,
      stepIndex: 0,
      lapses: 0,
      reviews: 0,
      lastReview: null,
      nextReview: new Date(),
      reviewHistory: []
    };
  }

  /**
   * Calculate next review time based on rating
   * This is the core Anki algorithm
   */
  calculateNextReview(card, rating, reviewTime = new Date()) {
    const updatedCard = { ...card };
    const oldState = card.state;

    // Add to review history
    updatedCard.reviewHistory.push({
      date: reviewTime,
      rating,
      oldInterval: card.interval,
      oldEase: card.ease,
      oldState: card.state
    });
    updatedCard.lastReview = reviewTime;
    updatedCard.reviews++;

    switch (card.state) {
      case CardState.NEW:
        this._reviewNew(updatedCard, rating);
        break;
      case CardState.LEARNING:
        this._reviewLearning(updatedCard, rating);
        break;
      case CardState.REVIEW:
        this._reviewReview(updatedCard, rating);
        break;
      case CardState.RELEARNING:
        this._reviewRelearning(updatedCard, rating);
        break;
    }

    // Calculate actual next review date
    updatedCard.nextReview = this._calculateNextReviewDate(updatedCard, reviewTime);

    return updatedCard;
  }

  /**
   * Review a NEW card
   */
  _reviewNew(card, rating) {
    switch (rating) {
      case Rating.AGAIN:
        // Card enters learning at the first step
        card.stepIndex = 0;
        card.state = CardState.LEARNING;
        break;
      case Rating.HARD:
        // Move to the next learning step (e.g., 10 minutes)
        if (card.stepIndex < this.config.newSteps.length - 1) {
          card.stepIndex++;
        }
        card.state = CardState.LEARNING;
        break;
      case Rating.GOOD:
        // Graduate the card to a review state
        card.state = CardState.REVIEW;
        card.interval = this.config.graduatingInterval;
        break;
      case Rating.EASY:
        // Graduate the card with a bonus
        card.state = CardState.REVIEW;
        card.interval = this.config.easyInterval;
        card.ease = this.config.startingEase + this.config.easyReward;
        break;
    }
  }

  /**
   * Review a LEARNING card
   */
  _reviewLearning(card, rating) {
    switch (rating) {
      case Rating.AGAIN:
        card.stepIndex = 0;
        break;
      case Rating.HARD:
        // Stay at current step
        break;
      case Rating.GOOD:
        if (card.stepIndex < this.config.newSteps.length - 1) {
          card.stepIndex++;
        } else {
          card.state = CardState.REVIEW;
          card.interval = this.config.graduatingInterval;
        }
        break;
      case Rating.EASY:
        card.state = CardState.REVIEW;
        card.interval = this.config.easyInterval;
        card.ease += this.config.easyReward;
        break;
    }
  }

  /**
   * Review a REVIEW card (the main Anki algorithm)
   */
  _reviewReview(card, rating) {
    switch (rating) {
      case Rating.AGAIN:
        // Lapse - failed the card
        card.lapses++;
        card.ease = Math.max(
          this.config.minimumEase,
          card.ease - 20
        );
        card.interval = Math.max(
          this.config.minimumInterval,
          card.interval * this.config.lapseNewInterval
        );
        card.state = CardState.RELEARNING;
        card.stepIndex = 0;
        break;

      case Rating.HARD:
        card.ease = Math.max(
          this.config.minimumEase,
          card.ease - this.config.easyPenalty
        );
        card.interval = card.interval * this.config.hardInterval * this.config.intervalModifier;
        break;

      case Rating.GOOD:
        card.interval = card.interval * (card.ease / 100) * this.config.intervalModifier;
        break;

      case Rating.EASY:
        card.ease += this.config.easyReward;
        card.interval = card.interval * (card.ease / 100) * this.config.easyBonus * this.config.intervalModifier;
        break;
    }

    // Apply maximum interval cap
    if (card.state === CardState.REVIEW) {
      card.interval = Math.min(
        this.config.maximumInterval,
        Math.max(this.config.minimumInterval, card.interval)
      );
    }
  }

  /**
   * Review a RELEARNING card
   */
  _reviewRelearning(card, rating) {
    switch (rating) {
      case Rating.AGAIN:
        card.stepIndex = 0;
        break;
      case Rating.HARD:
        // Stay at current step
        break;
      case Rating.GOOD:
        if (card.stepIndex < this.config.lapseSteps.length - 1) {
          card.stepIndex++;
        } else {
          card.state = CardState.REVIEW;
          // Interval was already reduced during lapse
        }
        break;
      case Rating.EASY:
        card.state = CardState.REVIEW;
        // Slightly increase interval for easy
        card.interval *= 1.2;
        break;
    }
  }

  /**
   * Calculate the actual next review date/time
   */
  _calculateNextReviewDate(card, now = new Date()) {
    const nextReview = new Date(now);

    if (card.state === CardState.LEARNING || card.state === CardState.RELEARNING) {
      // Use steps (in minutes)
      const steps = card.state === CardState.LEARNING
        ? this.config.newSteps
        : this.config.lapseSteps;

      const minutes = steps[Math.min(card.stepIndex, steps.length - 1)];
      nextReview.setMinutes(nextReview.getMinutes() + minutes);
    } else if (card.state === CardState.REVIEW) {
      // Use interval (in days)
      nextReview.setDate(nextReview.getDate() + Math.round(card.interval));
    }

    return nextReview;
  }

  /**
   * Get display text for next review time
   */
  getNextReviewDisplay(card, rating) {
    const tempCard = { ...card };
    const nextCard = this.calculateNextReview(tempCard, rating);
    const now = new Date();
    const next = nextCard.nextReview;
    const diffMs = next - now;
    const diffMins = Math.round(diffMs / 60000);
    const diffHours = Math.round(diffMs / 3600000);
    const diffDays = Math.round(diffMs / 86400000);

    if (diffMins < 1) return '<1m';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays === 1) return '1d';
    return `${diffDays}d`;
  }

  /**
   * Get all cards due for review
   */
/**
   * Get all cards due for review
   */
  getDueCards(cards, now = new Date()) {
    const currentTime = now instanceof Date && !isNaN(now.getTime()) ? now : new Date();

    return cards.filter(card => {
      if (!card) return false;

      // New cards are always due
      if (card.state === CardState.NEW || !card.nextReview) {
        return true;
      }

      let nextReviewDate;

      // This logic handles multiple date formats robustly.
      if (card.nextReview instanceof Date) {
        // It's already a JavaScript Date object
        nextReviewDate = card.nextReview;
      } else if (card.nextReview && typeof card.nextReview.toDate === 'function') {
        // It's a Firestore Timestamp from the database, so we convert it
        nextReviewDate = card.nextReview.toDate();
      } else {
        // Fallback for other formats like ISO strings
        try {
          nextReviewDate = new Date(card.nextReview);
        } catch (e) {
          console.error(`Could not create Date from card.nextReview for card ${card.id}`, e);
          return false; // On error, do NOT show the card
        }
      }

      // **THE CRITICAL FIX**: If the date is invalid for any reason, it is NOT due.
      if (!nextReviewDate || isNaN(nextReviewDate.getTime())) {
        console.warn(`Card ${card.id} has an invalid nextReview date. Hiding it from session.`);
        return false;
      }

      // The actual check: is the review time in the past or present?
      return nextReviewDate <= currentTime;
    });
  }

  /**
   * Sort cards by priority (Anki order)
   */
  sortByPriority(cards) {
    return cards.sort((a, b) => {
      // Priority: Failed cards > Learning > Review > New
      const priority = {
        [CardState.RELEARNING]: 0,
        [CardState.LEARNING]: 1,
        [CardState.REVIEW]: 2,
        [CardState.NEW]: 3
      };

      const aPriority = priority[a.state] ?? 4;
      const bPriority = priority[b.state] ?? 4;

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      // Within same priority, sort by due date
      const aTime = new Date(a.nextReview || 0).getTime();
      const bTime = new Date(b.nextReview || 0).getTime();
      return aTime - bTime;
    });
  }

  /**
   * Get button labels with time displays
   */
  getButtonLabels(card) {
    return {
      again: `Again (${this.getNextReviewDisplay(card, Rating.AGAIN)})`,
      hard: `Hard (${this.getNextReviewDisplay(card, Rating.HARD)})`,
      good: `Good (${this.getNextReviewDisplay(card, Rating.GOOD)})`,
      easy: `Easy (${this.getNextReviewDisplay(card, Rating.EASY)})`
    };
  }

  /**
   * Get session statistics
   */
  getSessionStats(reviewedCards) {
    return {
      total: reviewedCards.length,
      new: reviewedCards.filter(c => c.oldState === CardState.NEW).length,
      learning: reviewedCards.filter(c => c.oldState === CardState.LEARNING).length,
      review: reviewedCards.filter(c => c.oldState === CardState.REVIEW).length,
      relearning: reviewedCards.filter(c => c.oldState === CardState.RELEARNING).length,
      again: reviewedCards.filter(c => c.lastRating === Rating.AGAIN).length,
      hard: reviewedCards.filter(c => c.lastRating === Rating.HARD).length,
      good: reviewedCards.filter(c => c.lastRating === Rating.GOOD).length,
      easy: reviewedCards.filter(c => c.lastRating === Rating.EASY).length
    };
  }
}

// Export a default instance
export const srsService = new AnkiSRS();